<template>
  <div class="purchase-return-list">
    <div class="page-header">
      <h2>进货退货单</h2>
      <button class="btn-primary" @click="goToCreate">+ 新建退货单</button>
    </div>

    <div class="filter-bar">
      <el-select v-model="filters.status" placeholder="状态" clearable @change="loadData">
        <el-option label="待审核" value="PENDING_REVIEW" />
        <el-option label="已审核" value="REVIEWED" />
        <el-option label="已收货" value="RECEIVED" />
        <el-option label="已退款" value="REFUNDED" />
        <el-option label="已完成" value="COMPLETED" />
        <el-option label="已拒绝" value="REJECTED" />
        <el-option label="已取消" value="CANCELLED" />
      </el-select>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="yyyy-MM-dd"
        @change="handleDateChange"
      />
    </div>

    <el-table :data="list" v-loading="loading" border stripe>
      <el-table-column prop="returnNo" label="退货单号" width="180" />
      <el-table-column prop="purchaseNo" label="进货单号" width="180" />
      <el-table-column prop="supplierName" label="供应商" width="150" />
      <el-table-column label="退货商品" width="200">
        <template slot-scope="scope">
          <div v-for="item in scope.row.items" :key="item.id">
            {{ item.productName }} x {{ item.returnQuantity }}
          </div>
        </template>
      </el-table-column>
      <el-table-column prop="totalRefundAmount" label="退款金额" width="120">
        <template slot-scope="scope">
          ¥{{ scope.row.totalRefundAmount.toFixed(2) }}
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getStatusType(scope.row.status)">
            {{ getStatusText(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="180" />
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" @click="viewDetail(scope.row.id)">查看</el-button>
          <el-button
            v-if="scope.row.status === 'PENDING_REVIEW'"
            type="text"
            @click="approveReturn(scope.row)"
          >审核</el-button>
          <el-button
            v-if="scope.row.status !== 'REFUNDED' && scope.row.status !== 'CANCELLED' && scope.row.status !== 'REJECTED' && !isFullyRefunded(scope.row)"
            type="text"
            style="color: #e6a23c;"
            @click="openRefundDialog(scope.row)"
          >已收退款</el-button>
          <el-tag v-if="isFullyRefunded(scope.row)" type="success" size="mini">已收退款</el-tag>
        </template>
      </el-table-column>
    </el-table>

    <!-- 退款弹窗 -->
    <el-dialog title="退款流水录入" :visible.sync="showRefundDialog" width="500px" @close="closeRefundDialog">
      <el-form label-width="100px">
        <el-form-item>
          <el-checkbox v-model="refundForm.isReceived" @change="toggleRefundForm">已收款</el-checkbox>
        </el-form-item>

        <template v-if="refundForm.isReceived">
          <el-form-item label="收款类型">
            <el-select v-model="refundForm.refundType" placeholder="请选择">
              <el-option label="原路返回" value="ORIGINAL" />
              <el-option label="现金" value="CASH" />
              <el-option label="信用卡" value="CREDIT_CARD" />
              <el-option label="转账" value="BANK_TRANSFER" />
              <el-option label="PayNow" value="PAYNOW" />
            </el-select>
          </el-form-item>

          <el-form-item label="退款金额">
            <el-input-number v-model="refundForm.amount" :precision="2" :min="0.01" :step="1" style="width: 100%;" />
            <div class="form-hint">默认全额: ¥{{ currentReturnOrder ? currentReturnOrder.totalRefundAmount.toFixed(2) : '0.00' }}</div>
          </el-form-item>

          <el-form-item label="收款凭证">
            <el-upload
              action="/api/files/upload-image"
              :show-file-list="false"
              :on-success="handleRefundProofUpload"
              accept="image/*"
            >
              <el-button size="small" type="primary">上传凭证</el-button>
            </el-upload>
            <img v-if="refundForm.proofUrl" :src="refundForm.proofUrl" style="max-width: 200px; margin-top: 10px;" />
          </el-form-item>

          <el-form-item label="备注">
            <el-input v-model="refundForm.remark" type="textarea" :rows="2" placeholder="请输入备注" />
          </el-form-item>
        </template>
      </el-form>

      <div slot="footer">
        <el-button @click="showRefundDialog = false">取消</el-button>
        <el-button type="primary" @click="submitRefund">提交</el-button>
      </div>
    </el-dialog>

    <el-pagination
      @current-change="handlePageChange"
      :current-page="pagination.page"
      :page-size="pagination.size"
      :total="pagination.total"
      layout="total, prev, pager, next"
      class="pagination"
    />
  </div>
</template>

<script>
import { getPurchaseReturnList, approvePurchaseReturn, refundPurchaseReturn } from '@/api/purchaseReturn'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'PurchaseReturnList',
  data() {
    return {
      loading: false,
      list: [],
      filters: {
        status: ''
      },
      dateRange: [],
      pagination: {
        page: 1,
        size: 20,
        total: 0
      },
      showRefundDialog: false,
      currentReturnOrder: null,
      refundForm: {
        isReceived: true,
        refundType: 'ORIGINAL',
        amount: 0,
        proofUrl: '',
        remark: ''
      }
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          size: this.pagination.size
        }
        if (this.filters.status) {
          params.status = this.filters.status
        }
        if (this.dateRange && this.dateRange.length === 2) {
          params.startDate = this.dateRange[0]
          params.endDate = this.dateRange[1]
        }

        const res = await getPurchaseReturnList(params)
        this.list = res.data.content
        this.pagination.total = res.data.totalElements
      } catch (error) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    handleDateChange() {
      this.pagination.page = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.pagination.page = page
      this.loadData()
    },
    goToCreate() {
      this.$router.push('/wholesaler/purchase-return-create')
    },
    viewDetail(id) {
      this.$router.push(`/wholesaler/purchase-return-detail/${id}`)
    },
    async approveReturn(id) {
      try {
        await this.$confirm('确认通过该退货申请？', '提示', {
          type: 'warning'
        })
        await approvePurchaseReturn(id, { approved: true })
        this.$message.success('审核通过')
        this.loadData()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('审核失败')
        }
      }
    },
    getStatusType(status) {
      const map = {
        PENDING_REVIEW: 'warning',
        REVIEWED: 'primary',
        RECEIVED: 'info',
        REFUNDED: 'success',
        COMPLETED: 'success',
        REJECTED: 'danger',
        CANCELLED: 'info'
      }
      return map[status] || 'info'
    },
    getStatusText(status) {
      const map = {
        PENDING_REVIEW: '待审核',
        REVIEWED: '已审核',
        RECEIVED: '已收货',
        REFUNDED: '已退款',
        COMPLETED: '已完成',
        REJECTED: '已拒绝',
        CANCELLED: '已取消'
      }
      return map[status] || status
    },
    isFullyRefunded(row) {
      if (!row.totalRefundAmount || row.totalRefundAmount <= 0) return false
      const refunded = this.getRefundedAmount(row)
      return refunded >= row.totalRefundAmount
    },
    getRefundedAmount(row) {
      // 直接从退款流水汇总
      if (!row.refundRecords || row.refundRecords.length === 0) return 0
      return row.refundRecords.reduce((sum, record) => {
        return sum + (parseFloat(record.amount) || 0)
      }, 0)
    },
    openRefundDialog(row) {
      this.currentReturnOrder = row
      this.refundForm.isReceived = true
      this.refundForm.refundType = 'ORIGINAL'
      const refunded = this.getRefundedAmount(row)
      const remaining = (row.totalRefundAmount || 0) - refunded
      this.refundForm.amount = remaining > 0 ? remaining : 0
      this.refundForm.proofUrl = ''
      this.refundForm.remark = ''
      this.showRefundDialog = true
    },
    closeRefundDialog() {
      this.currentReturnOrder = null
      this.refundForm = {
        isReceived: true,
        refundType: 'ORIGINAL',
        amount: 0,
        proofUrl: '',
        remark: ''
      }
    },
    toggleRefundForm() {
      if (!this.refundForm.isReceived) {
        this.refundForm.refundType = 'ORIGINAL'
        this.refundForm.amount = 0
        this.refundForm.proofUrl = ''
        this.refundForm.remark = ''
      } else if (this.currentReturnOrder) {
        this.refundForm.amount = this.currentReturnOrder.totalRefundAmount || 0
      }
    },
    handleRefundProofUpload(response) {
      if (response && response.url) {
        this.refundForm.proofUrl = response.url
        this.$message.success('凭证上传成功')
      }
    },
    async submitRefund() {
      if (!this.currentReturnOrder) return

      if (this.refundForm.isReceived) {
        if (!this.refundForm.amount || this.refundForm.amount <= 0) {
          this.$message.error('请输入退款金额')
          return
        }
      }

      try {
        const userId = localStorage.getItem('user_id')
        const wholesalerId = getWholesalerId()
        const refundStatus = this.refundForm.isReceived ? 'completed' : 'pending'
        const refundAmount = this.refundForm.isReceived ? this.refundForm.amount : 0

        await refundPurchaseReturn(
          this.currentReturnOrder.id,
          wholesalerId,
          userId,
          this.refundForm.remark,
          this.refundForm.proofUrl,
          refundAmount,
          this.refundForm.refundType,
          refundStatus
        )

        this.$message.success(this.refundForm.isReceived ? '已收款，退款流水已录入' : '退款流水已录入，待收款')
        this.showRefundDialog = false
        this.loadData()
      } catch (error) {
        console.error('退款失败', error)
        this.$message.error('退款失败: ' + (error.response?.data?.message || error.message))
      }
    }
  }
}
</script>

<style scoped>
.purchase-return-list {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
}

.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}
</style>
